const LS_KEY = 'kro:device_id';
const COOKIE_KEY = 'kro_device_id';

export function getOrCreateDeviceId(): string {
  let id = readFromLocalStorage() || readFromCookie();
  if (!id) {
    id = uuidv4();
    writeToLocalStorage(id) || writeToCookie(id);
  }
  return id;
}

function readFromLocalStorage(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
  } catch {
    return null;
  }
}

function writeToLocalStorage(id: string): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    localStorage.setItem(LS_KEY, id);
    return true;
  } catch {
    return false;
  }
}

function readFromCookie(): string | null {
  try {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_KEY.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeToCookie(id: string): boolean {
  try {
    if (typeof document === 'undefined') return false;
    // 400 days max-age similar to long-lived cookies, secure if https
    const isSecure = location && location.protocol === 'https:';
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(id)}; path=/; max-age=${60 * 60 * 24 * 400}; ${isSecure ? 'Secure; ' : ''}SameSite=Lax`;
    return true;
  } catch {
    return false;
  }
}

// UUID v4 without external deps
export function uuidv4(): string {
  const cryptoObj = (globalThis as any).crypto || (globalThis as any).msCrypto;
  if (!cryptoObj || !cryptoObj.getRandomValues) {
    // very unlikely in modern browsers; fallback to Math.random (lower quality)
    const rnd = () => Math.floor(Math.random() * 0xffffffff);
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = rnd() & 0xf;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  const buf = new Uint8Array(16);
  cryptoObj.getRandomValues(buf);
  // Per RFC4122
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < buf.length; i++) {
    hex.push((buf[i] + 0x100).toString(16).substr(1));
  }
  return (
    hex[0] + hex[1] + hex[2] + hex[3] + '-' +
    hex[4] + hex[5] + '-' + hex[6] + hex[7] + '-' +
    hex[8] + hex[9] + '-' + hex[10] + hex[11] + hex[12] + hex[13] + hex[14] + hex[15]
  );
}

// Best-effort country detection based on browser locale settings.
// Returns ISO 3166-1 alpha-2 (e.g., 'US', 'AR') when available, otherwise undefined.
export function detectCountry(): string | undefined {
  try {
    // Collect potential BCP-47 locale tags from the environment
    const candidates: string[] = [];

    const nav: any = (typeof navigator !== 'undefined') ? navigator : undefined;
    if (nav) {
      if (Array.isArray(nav.languages) && nav.languages.length) {
        candidates.push(...nav.languages);
      }
      if (typeof nav.language === 'string') {
        candidates.push(nav.language);
      }
    }

    try {
      if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
        const loc = new Intl.DateTimeFormat().resolvedOptions().locale;
        if (loc) candidates.push(loc);
      }
    } catch {}

    // Prefer the first tag that includes an alpha-2 region
    for (const tag of candidates) {
      const region = extractRegionFromLocale(tag);
      if (region) return region;
    }

    // Try Intl.Locale if available as a last resort
    try {
      const I: any = (Intl as any);
      if (I && typeof I.Locale === 'function') {
        const first = candidates[0];
        if (first) {
          const loc = new I.Locale(first);
          const r = loc && typeof loc.region === 'string' ? String(loc.region).toUpperCase() : undefined;
          if (r && /^[A-Z]{2}$/.test(r)) return r;
        }
      }
    } catch {}
  } catch {}

  return undefined;
}

function extractRegionFromLocale(tag: string | undefined): string | undefined {
  if (!tag || typeof tag !== 'string') return undefined;
  // Normalize separators and split
  const parts = tag.replace(/_/g, '-').split('-');
  for (const raw of parts) {
    const p = raw.toUpperCase();
    // ISO 3166 alpha-2 region
    if (/^[A-Z]{2}$/.test(p)) return p;
    // Skip likely script (Titlecase) or language (lowercase) or numeric regions (e.g., 419)
    // We don't attempt to map numeric regions to countries.
  }
  // Fallback: regex search for -XX pattern
  const m = tag.toUpperCase().match(/-([A-Z]{2})\b/);
  return m ? m[1] : undefined;
}

// Best-effort OS detection using User-Agent Client Hints when available,
// otherwise falls back to classic UA / platform sniffing.
export function detectOS(): string | undefined {
  try {
    const nav: any = (typeof navigator !== 'undefined') ? navigator : undefined;
    if (!nav) return undefined;

    // Prefer User-Agent Client Hints
    const uad: any = (nav as any).userAgentData;
    if (uad && typeof uad.platform === 'string' && uad.platform) {
      // Common platforms: 'Windows', 'macOS', 'Android', 'Chrome OS', 'Linux', 'iOS'
      const platform = String(uad.platform).trim();
      if (platform) {
        // Normalize Chrome OS spelling
        if (/^chrome os$/i.test(platform)) return 'ChromeOS';
        return platform;
      }
    }

    // Fallback: navigator.platform
    const plat = typeof nav.platform === 'string' ? nav.platform : '';
    if (plat) {
      if (/^Mac/.test(plat)) return 'macOS';
      if (/^Win/.test(plat)) return 'Windows';
      if (/^Linux/.test(plat)) return 'Linux';
      if (/iPhone|iPad|iPod/i.test(plat)) return 'iOS';
    }

    // Fallback: navigator.userAgent
    const ua = typeof nav.userAgent === 'string' ? nav.userAgent : '';
    if (ua) {
      if (/Windows NT/i.test(ua)) return 'Windows';
      if (/Android/i.test(ua)) return 'Android';
      if (/iPhone|iPad|iPod|iOS/i.test(ua)) return 'iOS';
      if (/Mac OS X/i.test(ua)) return 'macOS';
      if (/CrOS/i.test(ua)) return 'ChromeOS';
      if (/Linux/i.test(ua)) return 'Linux';
    }
  } catch {}
  return undefined;
}
